import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { SectionShell, EmptyState } from "./common";
import { FormDialog } from "./FormDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { usePagedCollection } from "@/hooks/usePagedCollection";
import { api } from "@/lib/api";
import { analytics } from "@/lib/analytics";
import { REGIONS, REGION_ORDER } from "@/lib/ncRegions";
import { Briefcase, MapPin, ExternalLink, Search, X, Loader2, Building2, Handshake } from "lucide-react";

const TYPES = [["all", "All"], ["full-time", "Full-time"], ["part-time", "Part-time"], ["contract", "Contract"], ["internship", "Internship"]];
const VISA = [
  { key: "h1b",   label: "Sponsors H-1B" },
  { key: "gc",    label: "Green card sponsor" },
  { key: "opt",   label: "STEM OPT" },
  { key: "refer", label: "Can refer" },
];

const FIELDS = [
  { name: "title", label: "Job title", type: "text", required: true, placeholder: "e.g. Software Engineer II" },
  { name: "type", label: "Job type", type: "select", required: true, options: ["full-time", "part-time", "internship", "contract"], default: "full-time" },
  { name: "company", label: "Company", type: "text", required: true, placeholder: "e.g. Cisco Systems" },
  { name: "city", label: "City", type: "city", required: true, placeholder: "Start typing — Raleigh, Charlotte, RTP…" },
  { name: "desc", label: "Description", type: "textarea", required: true, rows: 4, placeholder: "What's the role? Who should apply? Anything a fellow community member should know." },
  { name: "apply_link", label: "Apply link (URL)", type: "text", required: true, placeholder: "https://…" },
  { name: "can_refer", label: "I can refer candidates for this role", type: "checkbox" },
  { name: "sponsors_h1b", label: "This employer sponsors H-1B", type: "checkbox" },
  { name: "sponsors_gc", label: "This employer sponsors green cards", type: "checkbox" },
  { name: "accepts_opt", label: "Accepts STEM OPT / F-1 students", type: "checkbox" },
  { name: "remote", label: "Remote-friendly", type: "checkbox" },
  { name: "poster_name", label: "Your name (optional)", type: "text", placeholder: "Shown on the listing" },
];

// ── Visa badge ───────────────────────────────────────────────────────────
const V = {
  yes:     { bg: "#EAF3DE", fg: "#27500A" },
  likely:  { bg: "#FAEEDA", fg: "#633806" },
  no:      { bg: "#F1EFE8", fg: "#5F5E5A" },
  unknown: null,
};
function VisaBadge({ label, value }) {
  const s = V[value];
  if (!s) return null;
  const txt = value === "yes" ? `${label} ✓` : value === "likely" ? `${label} likely` : `No ${label.toLowerCase()}`;
  return <span className="text-[10px] font-medium uppercase tracking-wider px-2 py-0.5 rounded-full whitespace-nowrap" style={{ background: s.bg, color: s.fg }}>{txt}</span>;
}

function JobCard({ j, i }) {
  const noSponsor = j.sponsors_h1b === "no";
  return (
    <motion.article initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: Math.min(i * 0.04, 0.3) }}
      className={`group rounded-2xl border bg-card p-5 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 flex flex-col gap-3 ${j.can_refer ? "border-primary/50" : "border-border"}`}>
      <div className="flex flex-wrap gap-1.5">
        {j.can_refer && <span className="text-[10px] font-medium uppercase tracking-wider px-2 py-0.5 rounded-full bg-primary text-primary-foreground inline-flex items-center gap-1"><Handshake className="w-3 h-3" />Can refer</span>}
        {noSponsor
          ? <VisaBadge label="Sponsorship" value="no" />
          : <>
              <VisaBadge label="H-1B" value={j.sponsors_h1b} />
              <VisaBadge label="Green card" value={j.sponsors_gc} />
              <VisaBadge label="STEM OPT" value={j.accepts_opt} />
            </>}
        {j.remote && <span className="text-[10px] font-medium uppercase tracking-wider px-2 py-0.5 rounded-full bg-muted text-muted-foreground">Remote</span>}
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-primary mb-1 truncate">{j.company}</p>
        <h3 className="font-serif text-lg font-semibold text-foreground leading-snug">{j.title}</h3>
      </div>
      <div className="flex items-center gap-3 flex-wrap text-xs text-muted-foreground">
        <span className="capitalize">{j.type}</span>
        <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{j.city}</span>
      </div>
      <p className="text-sm text-foreground/70 leading-relaxed line-clamp-3 flex-1">{j.desc}</p>
      {j.visa_note && <p className="text-[11px] text-muted-foreground leading-snug">{j.visa_note}</p>}
      <div className="mt-auto pt-3 border-t border-border flex items-center justify-between gap-2">
        <span className="text-[11px] text-muted-foreground truncate">
          {j.source === "manual" ? `posted by ${j.poster_name || "a member"}` : `via ${j.source_name || j.source}`} · {j.time}
        </span>
        {j.apply_link && (
          <a href={j.apply_link} target="_blank" rel="noopener noreferrer" onClick={() => analytics.clickOutbound(`job-apply-${j.source}`)}
            className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-foreground text-background px-4 py-1.5 text-xs font-medium hover:bg-foreground/90 transition-colors">
            Apply <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>
    </motion.article>
  );
}

// ── Sponsors table ───────────────────────────────────────────────────────
function SponsorsTab() {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => {
      setLoading(true);
      api.get("/sponsors", { params: { q: q || undefined, limit: 50 } })
        .then(({ data }) => { setRows(data.sponsors || []); setTotal(data.total || 0); })
        .catch(() => setRows([])).finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div>
      <div className="relative mb-4 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
        <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search employer — Cisco, SAS, Fidelity…" className="pl-9 h-10 rounded-xl" />
      </div>
      {loading ? <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
      : rows.length === 0 ? (
        <EmptyState icon={Building2} message={q ? `No NC sponsor named "${q}" in the latest DOL data.` : "Sponsor data hasn't been loaded yet. It comes from the U.S. Department of Labor's public H-1B and green-card filings."} />
      ) : (
        <>
          <p className="text-xs text-muted-foreground mb-3">{total.toLocaleString()} NC employers with H-1B or green-card filings · from U.S. Dept. of Labor disclosure data</p>
          <div className="overflow-x-auto rounded-2xl border border-border">
            <table className="w-full text-sm" style={{ tableLayout: "fixed" }}>
              <thead className="bg-muted/50 text-[11px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="text-left font-medium px-4 py-2.5 w-[34%]">Employer</th>
                  <th className="text-right font-medium px-3 py-2.5 w-[12%]">H-1B</th>
                  <th className="text-right font-medium px-3 py-2.5 w-[12%]">Green card</th>
                  <th className="text-left font-medium px-3 py-2.5 hidden md:table-cell">Common roles</th>
                  <th className="text-left font-medium px-3 py-2.5 w-[16%] hidden sm:table-cell">Where</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(s => (
                  <tr key={s.id} className="border-t border-border">
                    <td className="px-4 py-2.5 font-medium text-foreground truncate">{s.name}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{s.h1b_filings.toLocaleString()}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{s.perm_filings.toLocaleString()}</td>
                    <td className="px-3 py-2.5 text-muted-foreground truncate hidden md:table-cell">{(s.top_titles || []).slice(0, 3).join(", ")}</td>
                    <td className="px-3 py-2.5 text-muted-foreground truncate hidden sm:table-cell">{(s.cities || []).slice(0, 2).join(", ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-muted-foreground mt-3">Counts are certified filings at North Carolina worksites over the fiscal years shown in the data. A filing means the employer has sponsored before — not a guarantee they will for a given role.</p>
        </>
      )}
    </div>
  );
}

// ── Section ──────────────────────────────────────────────────────────────
export function JobsSection() {
  const [tab, setTab] = useState("jobs");
  const [type, setType] = useState("all");
  const [region, setRegion] = useState("all");
  const [visa, setVisa] = useState({ h1b: false, gc: false, opt: false, refer: false });
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");

  useEffect(() => { const t = setTimeout(() => setQuery(search.trim()), 350); return () => clearTimeout(t); }, [search]);

  const params = useMemo(() => {
    const p = {};
    if (type !== "all") p.type = type;
    if (region !== "all") p.region = region;
    if (query) p.q = query;
    for (const k of Object.keys(visa)) if (visa[k]) p[k] = true;
    return p;
  }, [type, region, query, visa]);

  const { items, total, hasMore, loading, loadingMore, loadMore, prepend } = usePagedCollection("jobs", params);
  const isFiltered = type !== "all" || region !== "all" || !!query || Object.values(visa).some(Boolean);
  const toggleVisa = (k) => { setVisa(v => ({ ...v, [k]: !v[k] })); analytics.filterJobs?.(k); };

  return (
    <SectionShell id="jobs" overline="Jobs Board" title="Opportunities from people who get it"
      subtitle="Real openings across NC with honest visa facts, plus referrals from members who'll vouch for you. Every listing links straight to the employer."
      action={<FormDialog resource="jobs" title="Post a job or referral" description="Know of an opening? Can you refer someone? Post it — it goes live after a quick check." fields={FIELDS} onCreated={prepend} submitLabel="Post job" testIdPrefix="job"
        trigger={<Button className="rounded-full px-6"><Briefcase className="w-4 h-4 mr-2" />Post a job</Button>} />}>

      {/* Jobs / Sponsors tabs */}
      <div className="flex items-center gap-1 mb-6 border-b border-border">
        {[["jobs", "Open roles"], ["sponsors", "Who sponsors in NC"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === k ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>{l}</button>
        ))}
      </div>

      {tab === "sponsors" ? <SponsorsTab /> : (
        <>
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search title, company — 'data engineer', 'Cisco', 'nurse'…" className="pl-9 pr-9 h-11 rounded-xl" aria-label="Search jobs" />
            {search && <button type="button" onClick={() => setSearch("")} aria-label="Clear" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>}
          </div>

          <div className="flex flex-wrap gap-2 mb-2">
            {TYPES.map(([k, l]) => <button key={k} onClick={() => setType(k)} className={`px-3.5 py-1.5 rounded-full text-[13px] font-medium border transition-colors ${type === k ? "bg-primary text-primary-foreground border-primary" : "text-muted-foreground border-border hover:text-foreground"}`}>{l}</button>)}
          </div>
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="text-[12px] text-muted-foreground mr-1">Near</span>
            {[["all", "All NC"], ...REGION_ORDER.map(k => [k, REGIONS[k].label]), ["remote", "Remote"]].map(([k, l]) => (
              <button key={k} onClick={() => setRegion(k)} className={`px-3 py-1 rounded-full text-[12px] font-medium border transition-colors ${region === k ? "bg-foreground text-background border-foreground" : "text-muted-foreground border-border hover:text-foreground"}`}>{l}</button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2 mb-6">
            <span className="text-[12px] text-muted-foreground mr-1">Visa</span>
            {VISA.map(v => (
              <button key={v.key} onClick={() => toggleVisa(v.key)} className={`px-3 py-1 rounded-full text-[12px] font-medium border transition-colors ${visa[v.key] ? "bg-foreground text-background border-foreground" : "text-muted-foreground border-border hover:text-foreground"}`}>{v.label}</button>
            ))}
            {!loading && <span className="text-[12px] text-muted-foreground ml-1">{total} {total === 1 ? "role" : "roles"}</span>}
            {isFiltered && <button onClick={() => { setType("all"); setRegion("all"); setSearch(""); setVisa({ h1b: false, gc: false, opt: false, refer: false }); }} className="text-[12px] text-primary hover:underline ml-auto">Clear filters</button>}
          </div>

          {loading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
          : items.length === 0 ? (
            <EmptyState icon={Briefcase} message={isFiltered ? "Nothing matches those filters yet. Try widening the region or clearing a visa filter." : "No roles listed yet. Know of an opening, or can you refer someone? Post it above."} />
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">{items.map((j, i) => <JobCard key={j.id} j={j} i={i} />)}</div>
              {hasMore && <div className="flex justify-center mt-10"><Button variant="outline" onClick={loadMore} disabled={loadingMore} className="rounded-full px-8">{loadingMore && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Load more roles</Button></div>}
            </>
          )}
          <p className="text-[11px] text-muted-foreground mt-8 leading-relaxed">
            <strong className="font-medium text-foreground">How visa badges work:</strong> "H-1B ✓" and "Green card ✓" mean the employer has certified filings at NC worksites in U.S. Department of Labor data, or the posting says so. "STEM OPT likely" is the one inference we make — H-1B sponsors are almost always E-Verify enrolled, which STEM OPT requires. "No sponsorship" means the posting itself says so. Anything we can't verify shows no badge rather than a guess.
          </p>
        </>
      )}
    </SectionShell>
  );
}
